import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import {
  Form,
  Label,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  Input,
} from "@altimateai/lego";
import { Card, CardContent, Button, toast } from "@altimateai/lego";
import { LoadingButton, Stack } from "@altimateai/lego";
import { useMutation } from '@tanstack/react-query';
import { Badge } from "@/uiCore";
import { InputPassword } from "@altimateai/lego";
import { getIconByDatastoreType } from "../../DatastoreImage";
import { addDatastore, testDatastoreConnectionDuringCreation } from "@/helpers/apis";
import { ConnectionProps } from "./types";
import { useForm } from "react-hook-form";
import {
  AddDatastoreRequest,
  DatastoreTypeEnum,
  PostgresDatastoreRequest,
} from "@/pages/datastores/types";
import { handleConnectionTestToast } from "./utils";

const formSchema = z.object({
  host: z.string().min(1, "Host is required"),
  port: z.string().min(1, "Port is required").regex(/^\d+$/, "Port must be a number"),
  database: z.string().min(1, "Database name is required"),
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

type PostgresConnectionData = z.infer<typeof formSchema>;

const PostgresConnection = ({ typeData, handleBack }: ConnectionProps): JSX.Element => {
  const form = useForm<PostgresConnectionData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      host: "",
      port: "",
      database: "",
      username: "",
      password: "",
    },
  });

  const { watch, formState } = form;
  const formData = watch();
  const navigate = useNavigate();
  const { mutate: testDatastoreConnection, isPending: isTestingConnection } = useMutation({
    mutationFn: (data: Record<string, unknown>) => testDatastoreConnectionDuringCreation(data),
    onSuccess: handleConnectionTestToast,
    onError: () => handleConnectionTestToast(),
  });

  const { mutate: createDatastore, isPending: isCreating } = useMutation({
    mutationFn: (data: AddDatastoreRequest) => addDatastore(data),
    onSuccess: () => {
      navigate("/settings/datastores");
    },
    onError: (err: unknown) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: (err as Error)?.message || "Unable to add Datastore!",
      });
    },
  });

  const handleTestConnection = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const data: Partial<PostgresDatastoreRequest> = {
      host: formData.host,
      port: formData.port,
      database: formData.database,
      user: formData.username,
      password: formData.password,
      ...typeData,
    };
    testDatastoreConnection(data);
  };

  const onSubmit = (data: PostgresConnectionData) => {
    const connectionData: PostgresDatastoreRequest = {
      host: data.host,
      port: data.port,
      databases: [data.database],
      user: data.username,
      password: data.password,
      ...typeData,
    };
    createDatastore(connectionData);
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justify="between" className="al-items-center">
          <h3>Connection</h3>
          <Badge variant="default" className={"al-m-2"} tooltip="">
            <Stack direction="row" className="al-items-center al-p-2">
              {getIconByDatastoreType({
                datastoreType: DatastoreTypeEnum.POSTGRES,
                className: "al-w-10 al-h-10 al-mr-3",
              })}
              <span className="al-text-sm">Postgres</span>
            </Stack>
          </Badge>
        </Stack>

        <form onSubmit={form.handleSubmit(onSubmit)} className="al-mt-6">
          <Form {...form}>
            <Stack gap="lg" direction="column">
              <FormField
                control={form.control}
                name="host"
                render={({ field }) => (
                  <FormItem>
                    <Label className="al-block" htmlFor="host">
                      Host
                    </Label>
                    <FormControl>
                      <Input id="host" placeholder="localhost" className="al-mt-1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="port"
                render={({ field }) => (
                  <FormItem>
                    <Label className="al-block" htmlFor="port">
                      Port
                    </Label>
                    <FormControl>
                      <Input id="port" placeholder="5432" className="al-mt-1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="database"
                render={({ field }) => (
                  <FormItem>
                    <Label className="al-block" htmlFor="database">
                      Database
                    </Label>
                    <FormControl>
                      <Input id="database" placeholder="postgres" className="al-mt-1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <Label className="al-block" htmlFor="username">
                      Username
                    </Label>
                    <FormControl>
                      <Input
                        id="username"
                        placeholder="postgres"
                        className="al-mt-1"
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <Label className="al-block" htmlFor="password">
                      Password
                    </Label>
                    <FormControl>
                      <InputPassword
                        id="password"
                        className="al-mt-1"
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Stack direction="row" justify={"between"}>
                <Button variant={"outline"} asChild>
                  <Link to="/settings/datastores">Cancel</Link>
                </Button>
                <Stack gap={"md"}>
                  <Button variant={"secondary"} onClick={handleBack}>
                    Back
                  </Button>
                  <LoadingButton
                    variant="default"
                    disabled={!formState.isValid}
                    onClick={handleTestConnection}
                    loading={isTestingConnection}
                  >
                    Test Connection
                  </LoadingButton>
                  <LoadingButton
                    variant="default"
                    type="submit"
                    loading={isCreating}
                    className="al-px-4"
                    disabled={!formState.isValid}
                  >
                    Finish
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </Form>
        </form>
      </CardContent>
    </Card>
  );
};

export default PostgresConnection;
